import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  inject,
} from '@angular/core';

type ScrollContainer = HTMLElement | Window;

@Component({
  selector: 'back-to-top',
  standalone: true,
  templateUrl: './back-to-top.html',
  styleUrl: './back-to-top.scss',
})
export class BackToTopComponent implements AfterViewInit, OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly zone = inject(NgZone);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private target: HTMLElement | null = null;
  private scrollContainer: ScrollContainer | null = null;
  private removeListener: (() => void) | null = null;

  showScrollTop = false;

  @Input()
  set scrollTarget(target: HTMLElement | null) {
    this.target = target;

    if (this.isBrowser) {
      this.listenForScroll();
    }
  }

  get scrollTarget(): HTMLElement | null {
    return this.target;
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) {
      return;
    }

    this.listenForScroll();
  }

  ngOnDestroy(): void {
    this.removeScrollListener();
  }

  scrollToTop(): void {
    if (!this.isBrowser) {
      return;
    }

    const container = this.target ?? this.scrollContainer ?? window;

    container.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private listenForScroll(): void {
    if (!this.isBrowser) {
      return;
    }

    this.removeScrollListener();
    this.scrollContainer = this.target ?? window;
    const container = this.scrollContainer;

    this.zone.runOutsideAngular(() => {
      const listener = () => this.updateVisibility(this.getScrollTop(container));

      container.addEventListener('scroll', listener, { passive: true });
      this.removeListener = () => container.removeEventListener('scroll', listener);
      this.updateVisibility(this.getScrollTop(container));
    });
  }

  private getScrollTop(container: ScrollContainer): number {
    return 'scrollY' in container
      ? container.scrollY || document.documentElement.scrollTop
      : container.scrollTop;
  }

  private updateVisibility(scrollTop: number): void {
    const shouldShow = scrollTop > 100;

    if (this.showScrollTop === shouldShow) {
      return;
    }

    this.zone.run(() => {
      this.showScrollTop = shouldShow;
      this.changeDetector.markForCheck();
    });
  }

  private removeScrollListener(): void {
    this.removeListener?.();
    this.removeListener = null;
  }
}
